import { useState } from 'react'
import MultiSelector from './MultiSelector'
import './IndustryMultiSelector.css'
import Checkbox from '../Checkbox/Checkbox'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Collapse from '@mui/material/Collapse'
import chevron from '../../assets/nuoli-alas-red.svg'

export default function IndustryMultiSelector(props) {
  const { handleChange, options, filters, ...rest } = props

  const [toggledIndustries, setToggledIndustries] = useState([])

  const isSelected = (val) => filters?.some((f) => f.value === val)

  const removeByValues = (arr, valuesSet) => (arr || []).filter((f) => !valuesSet.has(f.value))

  const onToggleIndustry = (industryValue) => {
    let newToggledIndustries = [...toggledIndustries]
    if (newToggledIndustries.includes(industryValue)) {
      newToggledIndustries = newToggledIndustries.filter((ind) => ind !== industryValue)
    } else {
      newToggledIndustries.push(industryValue)
    }
    setToggledIndustries(newToggledIndustries)
  }

  // Parent category checkbox click
  const onToggleParent = (parent) => {
    const children = parent.childIndustries || []
    const childVals = new Set(children.map((c) => c.value))
    const parentIsOn = isSelected(parent.value)

    let next = filters || []

    if (parentIsOn) {
      // Parent OFF (remove parent + children)
      next = removeByValues(next, new Set([parent.value, ...childVals]))
    } else {
      // Parent ON (remove any children if selected as the parent covers them all)
      next = removeByValues(next, childVals)
      next = next.filter((f) => f.value !== parent.value).concat([parent])
    }

    handleChange(next, 'industry', true)
  }

  // Child category checkbox click
  const onToggleChild = (child, parent) => {
    const parentIsOn = isSelected(parent.value)
    const siblings = parent.childIndustries || []
    const siblingVals = new Set(siblings.map((c) => c.value))

    let next = filters || []

    if (parentIsOn) {
      // Parent is ON => "break out" into explicit children,
      // excluding the toggled-off child
      const otherSiblings = siblings.filter((c) => c.value !== child.value)

      // Remove parent category
      next = next.filter((f) => f.value !== parent.value)
      // Clear any selected siblings
      next = removeByValues(next, siblingVals)
      // Add back other siblings
      next = next.concat(otherSiblings)
    } else {
      // Normal child toggle
      const childIsOn = isSelected(child.value)
      next = childIsOn ? next.filter((f) => f.value !== child.value) : next.concat([child])
    }

    handleChange(next, 'industry', true)
  }

  const renderOptions = () => {
    if (options) {
      return options.map((o) => (
        <Box key={o.value}>
          <Box sx={styles.industryMainItem}>
            <Checkbox
              label={o.label}
              id={o.label}
              onClick={() => onToggleParent(o)}
              checked={isSelected(o.value)}
            />

            {o?.childIndustries?.length ? (
              <ButtonBase
                sx={styles.arrowButton}
                onClick={() => onToggleIndustry(o?.value)}
                disableRipple
              >
                <Box
                  component='img'
                  alt='Näytä alavalinnat'
                  sx={{
                    ...styles.arrow,
                    transform: `rotate(${toggledIndustries?.includes(o?.value) ? 180 : 0}deg)`,
                  }}
                  src={chevron}
                />
              </ButtonBase>
            ) : null}
          </Box>

          {o?.childIndustries?.length > 0 && (
            <Collapse in={toggledIndustries?.includes(o?.value)}>
              {o?.childIndustries?.map((cInd) => (
                <Box key={cInd.value} sx={styles.childIndustryItem}>
                  <Checkbox
                    label={cInd.label}
                    id={cInd.label}
                    onClick={() => onToggleChild(cInd, o)}
                    // If parent is selected, all children are considered selected (on UI level)
                    checked={isSelected(o.value) || isSelected(cInd.value)}
                  />
                </Box>
              ))}
            </Collapse>
          )}
        </Box>
      ))
    }
    return null
  }

  return (
    <div className='industry-selector-wrapper'>
      <MultiSelector
        text='Toimiala'
        filters={filters}
        filter='industry'
        options={options}
        target='industry'
        handleChange={handleChange}
        {...rest}
      >
        <Box sx={styles.industriesContainer}>{renderOptions()}</Box>
      </MultiSelector>
    </div>
  )
}

const styles = {
  industriesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    maxWidth: '100%',
    maxHeight: '51rem',
    overflowY: 'auto',
    paddingBottom: '5rem',
    marginBottom: '2rem',
  },
  industryMainItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '0.5rem',
  },
  arrowButton: {
    padding: '2rem',
    marginLeft: '0.5rem',
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50%',
    backgroundColor: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  arrow: {
    width: '2rem',
    transition: 'transform 0.3s ease-in-out',
  },
  childIndustryItem: {
    marginLeft: '3rem',
    marginTop: '1rem',
    marginBottom: '1.5rem',
    '& > label > span': {},
  },
}
